import _ from 'lodash';

import type { Field, Form, FormResponse, FormResponses, FormResult, Pass } from '@sine/sine-core-api-types';

import type { PrePass } from '../../visit/signin/components/visitorCheckInModal.types';
import passService from '../pass/pass.service';
import type { PrefillFormQuery } from '../pass/pass.types';
import { FieldBooleanSwitch, FieldType } from './constants';
import type { FieldWithResponses } from './signin.types';
import type { FormWithResponses } from './signin.types';

export const eventTypes = {
  signin: 'formResponses',
  signout: 'formResponsesSignOut',
};

const formService = {
  checkFormVisitorTypes(fields: Array<Field> | undefined, visitorType: string) {
    return fields?.some((field) => field.visitorTypes.includes(visitorType));
  },

  serializeFormResponses(fields: Array<FieldWithResponses> = []) {
    return fields.reduce<Array<FormResponse>>((acc, field) => {
      if (
        field.value ||
        (field.type === FieldType.BOOLEAN_SWITCH && FieldBooleanSwitch.RESPONSE_VALUE_LIST.includes(field.value)) ||
        (field.type === FieldType.NUMBER && field.value === 0)
      ) {
        const contextualValue = field.type === FieldType.NUMBER ? String(field.value) : field.value;

        acc.push({
          id: field.id,
          value: contextualValue,
          type: field.type,
        });
      }
      return acc;
    }, []);
  },

  deserializeFormResponses(fields: Array<FormResponse> = []) {
    const formResponses: Array<FormResponse> = [];

    fields.forEach((field) => {
      if (!_.isUndefined(field.value)) {
        formResponses.push({
          id: field.id,
          value: field.type === FieldType.NUMBER ? Number(field.value) : field.value,
          type: field.type,
        });
      }
    });

    return formResponses;
  },

  async retrieveFormPrefill(siteId: string, pass: Pass | PrePass, event: keyof typeof eventTypes) {
    if (!pass.email && !pass.mobile && !pass.user) {
      return null;
    }

    const { user, email, mobile, visitorType } = pass;
    const params: PrefillFormQuery = {
      user,
      email,
      mobile,
      visitorType,
      fieldName: eventTypes[event],
    };

    return passService
      .prefillForm(siteId, params)
      .then((prefilledForm: FormResponses) => {
        prefilledForm.responses = this.deserializeFormResponses(prefilledForm.responses);
        return prefilledForm;
      })
      .catch(() => {
        return null;
      });
  },

  applyPrefilledFieldByVisitorType(prefilledForm: FormResponses | null, targetForm: Form, passVisitorType: string) {
    if (!prefilledForm?.responses.length) {
      return prefilledForm;
    }

    const visitorTypeQuestions = targetForm.fields.reduce<Array<Field['id']>>((prev, curr) => {
      if (curr.visitorTypes.includes(passVisitorType) && curr.prefillField) {
        prev.push(curr.id);
      }

      return prev;
    }, []);

    prefilledForm.responses = prefilledForm.responses.filter((response) => visitorTypeQuestions.includes(response.id));

    return prefilledForm;
  },

  applyInvitationFormResponseByVisitorType(prefilledForm: FormResult, targetForm: Form, passVisitorType: string) {
    if (!prefilledForm.fields?.length) {
      return prefilledForm;
    }

    const visitorTypeQuestions = targetForm.fields.reduce<Array<Field['id']>>((prev, curr) => {
      if (curr.visitorTypes.includes(passVisitorType)) {
        prev.push(curr.id);
      }

      return prev;
    }, []);

    const { fields, ...restPrefilledForm } = prefilledForm;

    const prefilledFormResponses = fields
      .filter((field) => visitorTypeQuestions.includes(field.id!))
      .map((_field) => {
        const { answer, ...restField } = _field;

        return {
          ...restField,
          value: answer,
        };
      });

    return { responses: prefilledFormResponses, ...restPrefilledForm };
  },

  applyFormToPass(formResponses: FormWithResponses, passProps: Pass) {
    passProps.formResponses = {
      id: formResponses.id,
      responses: this.serializeFormResponses(formResponses.fields),
    };

    return passProps;
  },
};

export default formService;
